import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const STORAGE_KEY = 'xelacore-settings';

export type Theme = 'light' | 'dark' | 'system';
export type LayoutMode = 'container' | 'fluid';
export type ViewProfile = 'standard' | 'dat';

export interface Settings {
  theme: Theme;
  layoutMode: LayoutMode;
  sidebarCollapsed: boolean;
  viewProfile: ViewProfile;
}

interface SettingsContextType {
  settings: Settings;
  setTheme: (theme: Theme) => void;
  setLayoutMode: (mode: LayoutMode) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setViewProfile: (profile: ViewProfile) => void;
  resolvedTheme: 'light' | 'dark';
}

const defaultSettings: Settings = {
  theme: 'system',
  layoutMode: 'container',
  sidebarCollapsed: false,
  viewProfile: 'standard',
};

const SettingsContext = createContext<SettingsContextType | null>(null);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultSettings, ...parsed };
      }
    } catch (e) {
      console.error('Failed to load settings from localStorage:', e);
    }
    return defaultSettings;
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    if (settings.theme === 'system') {
      return getSystemTheme();
    }
    return settings.theme;
  });

  const saveSettings = useCallback((newSettings: Settings) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
    } catch (e) {
      console.error('Failed to save settings to localStorage:', e);
    }
  }, []);

  useEffect(() => {
    const updateResolvedTheme = () => {
      const newResolvedTheme = settings.theme === 'system' ? getSystemTheme() : settings.theme;
      setResolvedTheme(newResolvedTheme);
    };

    updateResolvedTheme();

    if (settings.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => updateResolvedTheme();
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [settings.theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme]);

  const setTheme = useCallback((theme: Theme) => {
    setSettings((prev) => {
      const newSettings = { ...prev, theme };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  const setLayoutMode = useCallback((layoutMode: LayoutMode) => {
    setSettings((prev) => {
      const newSettings = { ...prev, layoutMode };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  const toggleSidebar = useCallback(() => {
    setSettings((prev) => {
      const newSettings = { ...prev, sidebarCollapsed: !prev.sidebarCollapsed };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  const setSidebarCollapsed = useCallback((sidebarCollapsed: boolean) => {
    setSettings((prev) => {
      const newSettings = { ...prev, sidebarCollapsed };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  const setViewProfile = useCallback((viewProfile: ViewProfile) => {
    setSettings((prev) => {
      const newSettings = { ...prev, viewProfile };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  useEffect(() => {
    document.documentElement.dataset.viewProfile = settings.viewProfile;
  }, [settings.viewProfile]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        setTheme,
        setLayoutMode,
        toggleSidebar,
        setSidebarCollapsed,
        setViewProfile,
        resolvedTheme,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
